/**
 * KayitAyarlariPage.jsx — Sistem Ayarları › Kayıt Ayarları (backlog #199, 2026-10-11)
 * Route: /Management/SistemAyarlari/Kayit   İzin: page.sistem.yonetim (yazma: SYSTEM_SETTINGS_MANAGE)
 *
 * İki kayıt kuralı, her biri iki kitle için ayrı açılıp kapanır — Yönetim (SAKA personeli) ve Firma
 * (firma kullanıcıları). Backend: Sistem Ayarları'nın `kayit` grubu (FDS-Backend #198,
 * SystemSettingConstant.KAYIT_*), istek anında okunur; yeniden başlatma gerekmez.
 *   - Vergi numarası ile personel ekleme: vergi numarası girilmemiş firmaya personel eklenemez,
 *     görevlendirilemez, taşınamaz (#40). Varsayılan: Yönetim kapalı (#65), Firma açık.
 *   - Firma belge doğrulaması: fuarın istediği firma belgeleri onaylanmamışsa kart 24 saat geçerli (#40).
 *     Varsayılan: ikisi de açık.
 * Varsayılanlar #198 öncesi davranışın aynısıdır. Her değişiklik geçmişe yazılır (kim, ne zaman, eski → yeni).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Container, Card, CardBody, CardHeader, Button, Badge, Alert, Spinner, Input, Table,
} from "reactstrap";
import { Save, RotateCcw, History, Info } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
// #430: the review of person.person_nationality_id that comes before the switch below is turned on
import UyrukKontrolKarti from "../../../Components/Kisi/UyrukKontrolKarti";
import {
  listSystemSettings, updateSystemSetting, getSystemSettingHistory, SETTING_GROUP_KAYIT, KAYIT_KEYS,
} from "../../../services/systemSettingsService";
import { useYetki, PERMISSION_KEYS } from "../../../services/rbacService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

/** İki kural, her biri iki kitle. Anahtarlar backend'deki SystemSettingConstant.KAYIT_* ile aynı. */
const KURALLAR = [
  {
    id: "vergiNo",
    get baslik() { return i18n.t("ayarlar:kayitAyarlari.vergiNumarasiIlePersonelEkleme"); },
    // WAS: aciklama: "Açıkken, vergi numarası girilmemiş bir firmaya personel eklenemez, görevlendirilemez ve "
    //   + "firmalar arasında taşınamaz.",
    get aciklama() { return i18n.t("ayarlar:kayitAyarlari.vergiNoAciklama"); }, // #349
    kitleler: [
      { key: KAYIT_KEYS.VERGI_NO_YONETIM, get ad() { return i18n.t("ayarlar:kayitAyarlari.yonetim"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.sakaPersonelininYaptigiIslemler"); } },
      { key: KAYIT_KEYS.VERGI_NO_FIRMA, get ad() { return i18n.t("ayarlar:kayitAyarlari.firma"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.firmaKullanicilarininYaptigiIslemler"); } },
    ],
  },
  {
    id: "firmaBelge",
    get baslik() { return i18n.t("ayarlar:kayitAyarlari.firmaBelgeDogrulamasi"); },
    // WAS: aciklama: "Açıkken, fuarın istediği firma belgeleri onaylanmamışsa basılan ya da görülen kart yalnızca "
    //   + "24 saat geçerlidir. Kapalıyken eksik firma belgeleri kartı kısaltmaz.",
    get aciklama() { return i18n.t("ayarlar:kayitAyarlari.firmaBelgeAciklama"); }, // #349
    kitleler: [
      { key: KAYIT_KEYS.FIRMA_BELGE_YONETIM, get ad() { return i18n.t("ayarlar:kayitAyarlari.yonetim"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.sakaPersonelininBastigiGorduguKart"); } },
      { key: KAYIT_KEYS.FIRMA_BELGE_FIRMA, get ad() { return i18n.t("ayarlar:kayitAyarlari.firma"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.firmaninBastigiGorduguKart"); } },
    ],
  },
  // #430 (2026-10-17): one setting, no audience split: a worker's personal documents follow HIS nationality instead of his company's
  {
    id: "kisiUyruk",
    get baslik() { return i18n.t("ayarlar:kayitAyarlari.kisininUyruguEsasAlinsin"); },
    get aciklama() { return i18n.t("ayarlar:kayitAyarlari.kisiUyrukAciklama"); },
    kitleler: [
      { key: KAYIT_KEYS.KISI_UYRUK_ESASI, get ad() { return i18n.t("ayarlar:kayitAyarlari.kisininUyrugu"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.butunKartlarVeBelgeKontrolleri"); } },
    ],
  },
  // #748 (backend SystemSettingConstant.KAYIT_KISI_TELEFONSUZ_EKLEME): one setting, staff only: a company login always types a phone
  {
    id: "kisiTelefonsuz",
    get baslik() { return i18n.t("ayarlar:kayitAyarlari.kisiTelefonsuzBaslik"); },
    get aciklama() { return i18n.t("ayarlar:kayitAyarlari.kisiTelefonsuzAciklama"); },
    kitleler: [
      { key: KAYIT_KEYS.KISI_TELEFONSUZ_EKLEME, get ad() { return i18n.t("ayarlar:kayitAyarlari.yonetim"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.kisiTelefonsuzAlt"); } },
    ],
  },
  // 2026-10-28: one setting, no audience split: the company portal's guided tour for Katılımcı logins, on or off for everyone (Components/Firma/FirmaEgitimi.jsx)
  {
    id: "portalEgitim",
    get baslik() { return i18n.t("ayarlar:kayitAyarlari.portalEgitimBaslik"); },
    get aciklama() { return i18n.t("ayarlar:kayitAyarlari.portalEgitimAciklama"); },
    kitleler: [
      { key: KAYIT_KEYS.PORTAL_EGITIM, get ad() { return i18n.t("ayarlar:kayitAyarlari.portalEgitimAd"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.portalEgitimAlt"); } },
    ],
  },
  // #704 (2026-11-03, backend #703): the disaster switch, next to the portal tour (Deniz). One setting, every company login; staff are never
  // affected. The backend applies it on the next request; an open company portal picks it up within a few minutes (PortalContext).
  {
    id: "sadePanel",
    get baslik() { return i18n.t("ayarlar:kayitAyarlari.sadePanelBaslik"); },
    get aciklama() { return i18n.t("ayarlar:kayitAyarlari.sadePanelAciklama"); },
    kitleler: [
      { key: KAYIT_KEYS.PORTAL_SADE_PANEL, get ad() { return i18n.t("ayarlar:kayitAyarlari.sadePanelAd"); }, get alt() { return i18n.t("ayarlar:kayitAyarlari.sadePanelAlt"); } },
    ],
  },
];

/** key → "Kural — Kitle", geçmiş tablosu için. */
// WAS: const AYAR_ADI = Object.fromEntries(KURALLAR.flatMap((k) => k.kitleler.map((x) => [x.key, `${k.baslik} — ${x.ad}`])));
// #349: built when asked, not at import: the names are translated getters, and a map made at import would keep
// the language that was active when the module loaded.
const ayarAdi = (key) => {
  for (const k of KURALLAR) for (const x of k.kitleler) if (x.key === key) return `${k.baslik} — ${x.ad}`;
  return null;
};

const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleString(currentLocaleTag());
};
const acikKapali = (v) => (v === true ? i18n.t("ayarlar:kayitAyarlari.acik") : v === false ? i18n.t("ayarlar:kayitAyarlari.kapali") : "—");

function KuralKarti({ kural, ayarlar, yazabilir, onSaved }) {
  const { t: tx } = useTranslation("ayarlar");
  const kayitli = (key) => ayarlar[key]?.value === true;
  const [taslak, setTaslak] = useState(() => Object.fromEntries(kural.kitleler.map((x) => [x.key, kayitli(x.key)])));
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [kaydedildi, setKaydedildi] = useState(false);

  // Kaydedilen değer değişince (burada ya da varsayılana dönünce) taslağı yeniden doldur — React'in
  // "prop değişince state'i ayarla" kalıbı, effect değil.
  const kaynak = JSON.stringify(kural.kitleler.map((x) => ayarlar[x.key]?.value));
  const [kaynakIcin, setKaynakIcin] = useState(kaynak);
  if (kaynakIcin !== kaynak) {
    setKaynakIcin(kaynak);
    setTaslak(Object.fromEntries(kural.kitleler.map((x) => [x.key, kayitli(x.key)])));
  }

  const degisenler = kural.kitleler.filter((x) => ayarlar[x.key] && taslak[x.key] !== kayitli(x.key));
  const varsayilanda = kural.kitleler.every((x) => ayarlar[x.key]?.isDefault !== false);

  async function kaydet(degerler) {
    setKaydediyor(true);
    setHata(null);
    setKaydedildi(false);
    try {
      // Her anahtar ayrı bir PUT (backend her biri için ayrı geçmiş satırı yazar). Değişmeyen gönderilmez.
      for (const [key, value] of degerler) {
        onSaved(await updateSystemSetting(key, value));
      }
      setKaydedildi(true);
    } catch (e) {
      setHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  return (
    <Card className="mb-3">
      <CardHeader className="py-2 fw-semibold">{kural.baslik}</CardHeader>
      <CardBody>
        <p className="text-muted small mb-3">{kural.aciklama}</p>
        <div className="d-flex flex-column gap-3">
          {kural.kitleler.map((x) => {
            const ayar = ayarlar[x.key];
            if (!ayar) {
              return (
                <div key={x.key} className="small text-warning">
                  {tx("kayitAyarlari.ayarBackendDeBulunamadi", { ad: x.ad })}<code>{x.key}</code>{tx("kayitAyarlari.backend198IleYenidenBaslatildi")}
                </div>
              );
            }
            const degisti = taslak[x.key] !== kayitli(x.key);
            return (
              <div key={x.key} className="d-flex flex-wrap align-items-start gap-3">
                <div className="form-check form-switch mb-0" style={{ minWidth: 0 }}>
                  <Input
                    type="switch"
                    role="switch"
                    id={`kayit-${x.key}`}
                    className="form-check-input"
                    checked={taslak[x.key]}
                    disabled={!yazabilir || kaydediyor}
                    onChange={(e) => { setTaslak((t) => ({ ...t, [x.key]: e.target.checked })); setKaydedildi(false); }}
                  />
                  <label className="form-check-label fw-semibold" htmlFor={`kayit-${x.key}`}>
                    {x.ad}: {acikKapali(taslak[x.key])}
                    {degisti && <Badge color="warning" className="ms-2">{tx("kayitAyarlari.kaydedilmedi")}</Badge>}
                    {ayar.isDefault && !degisti && <Badge color="light" className="ms-2 text-muted border">{tx("kayitAyarlari.varsayilan")}</Badge>}
                  </label>
                  <div className="text-muted small">{x.alt}{tx("kayitAyarlari.varsayilan2", { acikKapali: acikKapali(ayar.defaultValue) })}</div>
                </div>
                <div className="ms-auto text-muted small text-end">
                  {ayar.updatedAt
                    ? <>{tx("kayitAyarlari.sonDegisiklik")}{" "}{fmtDate(ayar.updatedAt)}{ayar.updatedByName ? ` · ${ayar.updatedByName}` : ""}</>
                    : tx("kayitAyarlari.hicDegistirilmedi")}
                </div>
              </div>
            );
          })}
        </div>
        {hata && <Alert color="danger" className="py-2 small mt-3 mb-0">{hata}</Alert>}
        {yazabilir && (
          <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
            <Button color="primary" size="sm" disabled={kaydediyor || degisenler.length === 0}
              onClick={() => kaydet(degisenler.map((x) => [x.key, taslak[x.key]]))}>
              {kaydediyor ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}{tx("common:kaydet")}
            </Button>
            <Button color="light" size="sm" disabled={kaydediyor || degisenler.length === 0}
              onClick={() => setTaslak(Object.fromEntries(kural.kitleler.map((x) => [x.key, kayitli(x.key)])))}>
              {tx("kayitAyarlari.vazgec")}
            </Button>
            <Button color="light" size="sm" disabled={kaydediyor || varsayilanda}
              title={tx("kayitAyarlari.ikiKitleyiDeBackendIn")}
              onClick={() => kaydet(kural.kitleler
                .filter((x) => ayarlar[x.key] && !ayarlar[x.key].isDefault)
                .map((x) => [x.key, ayarlar[x.key].defaultValue]))}>
              <RotateCcw size={14} className="me-1" />{tx("kayitAyarlari.varsayilanaDon")}
            </Button>
            {kaydedildi && <span className="text-success small">{tx("kayitAyarlari.kaydedildi")}</span>}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Gecmis({ yenile }) {
  const { t } = useTranslation("ayarlar");
  const [satirlar, setSatirlar] = useState(null);
  const [hata, setHata] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        // Dört anahtarın geçmişi (her biri en yeni 50), tek listede tarihe göre.
        const listeler = await Promise.all(Object.values(KAYIT_KEYS).map((k) => getSystemSettingHistory(k)));
        const hepsi = listeler.flat().sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt) || (b.id - a.id));
        if (!ignore) { setSatirlar(hepsi); setHata(null); }
      } catch (e) {
        if (!ignore) { setHata(e.message); setSatirlar([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [yenile]);

  return (
    <Card className="mb-3">
      <CardHeader className="py-2 fw-semibold d-flex align-items-center gap-2">
        <History size={16} />{" "}{t("kayitAyarlari.degisiklikGecmisi")}
      </CardHeader>
      <CardBody>
        {hata && <Alert color="warning" className="py-2 small">{hata}</Alert>}
        {satirlar == null ? (
          <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
        ) : satirlar.length === 0 ? (
          !hata && <div className="text-muted small">{t("kayitAyarlari.kayitAyarlariHicDegistirilmediHepsi")}</div>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="mb-0 small align-middle">
              <thead><tr><th>{t("kayitAyarlari.tarih")}</th><th>{t("kayitAyarlari.ayar")}</th><th>{t("kayitAyarlari.kim")}</th><th>{t("kayitAyarlari.eski")}</th><th>{t("kayitAyarlari.yeni")}</th></tr></thead>
              <tbody>
                {satirlar.map((h) => (
                  <tr key={`${h.key}-${h.id}`}>
                    <td className="text-nowrap">{fmtDate(h.changedAt) || "—"}</td>
                    {/* WAS: <td>{AYAR_ADI[h.key] || h.key}</td> */}
                    <td>{ayarAdi(h.key) || h.key}</td>
                    <td>{h.changedByName || (h.changedBy != null ? `#${h.changedBy}` : "—")}</td>
                    <td>{h.oldValue == null ? <span className="text-muted">{t("kayitAyarlari.varsayilan")}</span> : acikKapali(h.oldValue)}</td>
                    <td className="fw-semibold">{acikKapali(h.newValue)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default function KayitAyarlariPage() {
  const { t } = useTranslation("ayarlar");
  const [ayarlar, setAyarlar] = useState(null);
  const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
  const [gecmisSurumu, setGecmisSurumu] = useState(0);
  // Yazma ve geçmiş SYSTEM_SETTINGS_MANAGE ister (backend); sayfayı açan ama yetkisi olmayan salt okur.
  const yazabilir = useYetki(PERMISSION_KEYS.SYSTEM_SETTINGS_MANAGE);

  useEffect(() => { document.title = i18n.t("ayarlar:kayitAyarlari.kayitAyarlariFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await listSystemSettings(SETTING_GROUP_KAYIT);
        if (!ignore) setAyarlar(Object.fromEntries(list.map((s) => [s.key, s])));
      } catch (e) {
        if (!ignore) { setYuklemeHatasi(e.message); setAyarlar({}); }
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  const onSaved = useCallback((guncel) => {
    if (!guncel?.key) return;
    setAyarlar((a) => ({ ...a, [guncel.key]: guncel }));
    setGecmisSurumu((n) => n + 1);
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("kayitAyarlari.kayitAyarlari")} pageTitle={t("kayitAyarlari.sistemAyarlari")} />
        <Alert color="info" className="d-flex gap-2 align-items-start small">
          <Info size={16} className="flex-shrink-0 mt-1" />
          <div>
            {t("kayitAyarlari.personelKaydiVeKartGecerliligi")}{" "}<strong>{t("kayitAyarlari.yonetim")}</strong>{" "}{t("kayitAyarlari.sakaPersoneliVe")}
            <strong>{" "}{t("kayitAyarlari.firma")}</strong>{" "}{t("kayitAyarlari.firmaKullanicilariIcinAyriAcilip")}
          </div>
        </Alert>
        {yuklemeHatasi && <Alert color="danger">{yuklemeHatasi}</Alert>}
        {yazabilir === false && (
          <Alert color="warning" className="small">
            {t("kayitAyarlari.buAyarlariDegistirmekVeGecmisini")}{" "}<strong>{t("kayitAyarlari.sistemAyarlariYonetimi")}</strong>{" "}{t("kayitAyarlari.yetkisiGerekir")}
          </Alert>
        )}
        {ayarlar == null ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <>
            {KURALLAR.map((k) => (
              <KuralKarti key={k.id} kural={k} ayarlar={ayarlar} yazabilir={yazabilir === true} onSaved={onSaved} />
            ))}
            {/* #430: look at the nationality field before the switch above goes on (staff with the Kişiler page see it) */}
            <UyrukKontrolKarti />
            {yazabilir === true && <Gecmis yenile={gecmisSurumu} />}
          </>
        )}
      </Container>
    </div>
  );
}
